import { Component, computed, inject, input, ChangeDetectionStrategy } from '@angular/core';
import { EmptyResultComponent, type IInfiniteScrollCollectionVm } from '@okcargo-apps/layout-ionic';
import { DecaDocumentCompanyRole, IDecaDocument, isDecaShipperTaxIdBlocked } from '@okcargo-apps/shared-domain';
import { AccountService } from '../../account';
import { DecaListItemComponent } from '../deca-list-item/deca-list-item.component';
import { DecaStoreService } from '../deca-store.service';

export type DecaTodayListCategory = DecaDocumentCompanyRole | 'TODAY_DOCUMENTS_AS_DRIVER';

@Component({
  selector: 'lib-deca-today-list',
  templateUrl: './deca-today-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DecaListItemComponent, EmptyResultComponent],
})
export class DecaTodayListComponent {
  private readonly decaStoreService = inject(DecaStoreService);
  private readonly accountService = inject(AccountService);

  protected readonly userCompanyId = computed(() => this.accountService.account().company.id);

  public readonly category = input.required<DecaTodayListCategory>();
  public readonly detailRoute = input.required<string>();

  /**
   * Documents whose contractual shipper taxId is in the changes-blocked set cannot be managed
   * from this app. The consumer hosts opt in (e.g. the carriers dashboard); other hosts (docs-app)
   * keep showing them, because a carrier may still need to see them there.
   */
  public readonly hideChangesBlockedShipperTaxIds = input<boolean>(false);

  protected readonly documents = computed(() => {
    const vm = this.collectionVm();
    if (!this.hideChangesBlockedShipperTaxIds()) return vm;

    const items = vm.items.filter((document) => !isDecaShipperTaxIdBlocked(document.shipper.taxId));
    return { ...vm, items, totalElements: items.length };
  });

  private collectionVm(): IInfiniteScrollCollectionVm<IDecaDocument> {
    switch (this.category()) {
      case 'AS_SHIPPER':
        return this.decaStoreService.todayDocumentsAsShipper();
      case 'AS_CARRIER':
        return this.decaStoreService.todayDocumentsAsCarrier();
      case 'TODAY_DOCUMENTS_AS_DRIVER':
        return this.decaStoreService.todayDocumentsAsDriver();
    }
  }
}
